import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms'
import { AuthFlowResult, AuthFlowStatus, CognitoService } from '../../services/cognito.service';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

enum SignInStep {
  Credentials = 1,
  EmailOtp = 2,
}


@Component({
  selector: 'app-login',
  imports: [
    FormsModule, CommonModule, ProgressSpinnerModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent {
  @ViewChildren('otpInput') otpInputs?: QueryList<ElementRef<HTMLInputElement>>;

  readonly SignInStep = SignInStep;

  emailaddress: string = '';
  password: string = '';
  showPassword: any;
  showErrorAlert: boolean = false;
  successLogin: boolean = false;
  currentStep: SignInStep = SignInStep.Credentials;
  otpDigits: string[] = ['', '', '', '', '', ''];
  loading: boolean = false;
  isVerifyingOtp: boolean = false;
  errorMessage: string = 'Usuario y/o contraseña incorrectos';
  private authFlowSubscription?: Subscription;

  constructor(private authService: CognitoService) {
  }

  ngOnInit(): void {
    const pendingResult = this.authService.consumePendingAuthResult();
    if (pendingResult?.status === AuthFlowStatus.EmailOtpRequired) {
      this.showEmailOtpStep();
    }

    this.authFlowSubscription = this.authService.authFlow$.subscribe((result) => {
      if (result.status === AuthFlowStatus.EmailOtpRequired) {
        this.showEmailOtpStep();
      }
    });
  }

  ngOnDestroy(): void {
    this.authFlowSubscription?.unsubscribe();
  }

  async onSignIn(form: NgForm) {
    if (!form.valid || this.loading) {
      return;
    }

    this.loading = true;
    this.showErrorAlert = false;
    const password = this.password;
    this.password = '';

    try {
      const result = await this.authService.login(this.emailaddress, password);
      this.handleAuthResult(result);
    } catch {
      this.showError('Usuario y/o contraseña incorrectos');
    } finally {
      this.loading = false;
    }
  }

  onOtpInput(event: Event, index: number): void {
    if (this.isVerifyingOtp) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');

    if (digits.length > 1) {
      this.applyPastedOtp(digits);
      return;
    }

    this.otpDigits[index] = digits.slice(0, 1);
    input.value = this.otpDigits[index];

    if (this.otpDigits[index] && index < this.otpDigits.length - 1) {
      this.focusOtp(index + 1);
    }

    this.trySubmitOtp();
  }

  onOtpKeyDown(event: KeyboardEvent, index: number): void {
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'];

    if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
      event.preventDefault();
      this.focusOtp(index - 1);
      return;
    }

    if (!allowedKeys.includes(event.key) && !/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    if (this.isVerifyingOtp) {
      return;
    }

    const pastedCode = event.clipboardData?.getData('text') ?? '';
    this.applyPastedOtp(pastedCode);
  }

  async submitEmailOtp(): Promise<void> {
    if (this.loading || this.isVerifyingOtp) {
      return;
    }

    const code = this.otpDigits.join('');
    if (code.length !== 6) {
      return;
    }

    this.loading = true;
    this.isVerifyingOtp = true;
    this.showErrorAlert = false;

    try {
      const result = await this.authService.confirmEmailOtp(code);
      this.handleAuthResult(result);
    } catch {
      this.showOtpError('No se pudo completar la autenticación. Intenta nuevamente.');
    } finally {
      this.loading = false;
      this.isVerifyingOtp = false;
    }
  }

  private handleAuthResult(result: AuthFlowResult): void {
    switch (result.status) {
      case AuthFlowStatus.Authenticated:
        this.successLogin = true;
        break;
      case AuthFlowStatus.EmailOtpRequired:
        this.showEmailOtpStep();
        break;
      case AuthFlowStatus.NewPasswordRequired:
        break;
      case AuthFlowStatus.UnsupportedChallenge:
      case AuthFlowStatus.Error:
        if (this.currentStep === SignInStep.EmailOtp && !result.restartRequired) {
          this.showOtpError(result.message ?? 'No se pudo completar la autenticación. Intenta nuevamente.');
        } else {
          this.resetToCredentials();
          this.showError(result.message ?? 'Usuario y/o contraseña incorrectos');
        }
        break;
    }
  }

  private showEmailOtpStep(): void {
    this.currentStep = SignInStep.EmailOtp;
    this.showErrorAlert = false;
    this.resetOtp(false);
    this.focusOtp(0);
  }

  private trySubmitOtp(): void {
    if (this.otpDigits.join('').length === 6) {
      void this.submitEmailOtp();
    }
  }

  private applyPastedOtp(value: string): void {
    const normalizedValue = value.replace(/\D/g, '').slice(0, 6);
    if (!normalizedValue) {
      return;
    }

    const digits = normalizedValue.split('');
    this.otpDigits = ['', '', '', '', '', ''];

    digits.forEach((digit, index) => {
      this.otpDigits[index] = digit;
    });

    const nextEmptyIndex = this.otpDigits.findIndex((digit) => !digit);
    this.focusOtp(nextEmptyIndex === -1 ? this.otpDigits.length - 1 : nextEmptyIndex);
    this.trySubmitOtp();
  }

  private showError(message: string): void {
    this.errorMessage = message;
    this.showErrorAlert = true;
  }

  private showOtpError(message: string): void {
    this.showError(message);
    this.resetOtp();
  }

  private resetToCredentials(): void {
    this.currentStep = SignInStep.Credentials;
    this.resetOtp(false);
  }

  private resetOtp(shouldFocus = true): void {
    this.otpDigits = ['', '', '', '', '', ''];
    if (shouldFocus) {
      this.focusOtp(0);
    }
  }

  private focusOtp(index: number): void {
    setTimeout(() => {
      this.otpInputs?.get(index)?.nativeElement.focus();
    });
  }
}


